import {
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  StateKey,
  TransferState,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, defer, map, Observable, of, shareReplay } from 'rxjs';
import { ApiService } from '../../../shared/services/api.service';
import { withTransferState } from '../../../shared/utils/transfer-state.util';
import { PublicGameSection, PublicGamesIndexPayload, PublicGameSectionSummary } from './game-section.model';

// Claves de TransferState. La del detalle se compone con el slug: cada landing
// es una página distinta y el server puede haber serializado solo la suya.
const GAMES_INDEX_KEY = makeStateKey<PublicGameSectionSummary[]>('perron.games.index');

function gameSectionKey(slug: string): StateKey<PublicGameSection | null> {
  return makeStateKey<PublicGameSection | null>(`perron.games.section.${slug}`);
}

/**
 * Cliente de los endpoints públicos de secciones de juego
 * (`GET /api/web/games` y `GET /api/web/games/{slug}`). Mismo patrón que
 * `HomeBlocksService`: `providedIn:'root'`, `withTransferState` para que el
 * cliente hidrate con lo que ya pidió el render server (la request lleva
 * `Authorization` y queda fuera del transfer-cache HTTP automático de Angular),
 * y `shareReplay(1)` para que varias suscripciones a la misma URL no disparen
 * dos requests.
 *
 * Estas landings son páginas de SEO servidas por SSR en cada request
 * (`app.routes.server.ts` es catch-all `RenderMode.Server`), así que el
 * TransferState no es una optimización marginal: evita el "flash"
 * data → skeleton justo después de hidratar.
 *
 * **404 → `null`** (patrón `CategoryService.getCategoryBySlugRemote`): desde la
 * tienda, "no existe", "despublicada" y "borrada" son la misma situación —esa
 * landing no existe— y quien llama distingue así el 404 de un fallo de red,
 * que sí se propaga como error.
 */
@Injectable({ providedIn: 'root' })
export class GameSectionService {
  private readonly api = inject(ApiService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly transferState = inject(TransferState);

  private readonly cachedSections = new Map<string, Observable<PublicGameSection | null>>();
  private cachedIndex$?: Observable<PublicGameSectionSummary[]>;

  /**
   * Índice ligero de secciones publicadas. Sin consumidor todavía: su sitio
   * natural es el header/megamenú y entra con el bloque de navegación. Se
   * publica ya para que el contrato del endpoint no quede sin espejo en el front.
   */
  getGames(): Observable<PublicGameSectionSummary[]> {
    if (!this.cachedIndex$) {
      // `defer` para que la request solo se dispare en un MISS del TransferState.
      this.cachedIndex$ = withTransferState(
        this.transferState,
        this.isBrowser,
        GAMES_INDEX_KEY,
        defer(() =>
          this.api
            .get<PublicGamesIndexPayload>('/web/games')
            .pipe(map((res) => (Array.isArray(res?.games) ? res.games : []))),
        ),
      ).pipe(shareReplay(1));
    }
    return this.cachedIndex$;
  }

  /** Payload hidratado de una sección publicada. `null` si no existe (404). */
  getBySlug(slug: string): Observable<PublicGameSection | null> {
    const cached = this.cachedSections.get(slug);
    if (cached) return cached;

    const request$ = withTransferState(
      this.transferState,
      this.isBrowser,
      gameSectionKey(slug),
      defer(() =>
        this.api.get<PublicGameSection>(`/web/games/${slug}`).pipe(
          catchError((err: unknown) => {
            if (err instanceof HttpErrorResponse && err.status === 404) {
              return of(null);
            }
            throw err;
          }),
        ),
      ),
    ).pipe(shareReplay(1));

    this.cachedSections.set(slug, request$);
    return request$;
  }
}
